/* SIZESOFTX
=========================================================================================
* Descripción: Administración de campañas promocionales ARSync y prospectos CRM.
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1        | 14/09/2026 | José Luis Aragón   Cervantes     | Creación FASE 2 CRM.
*      2        | 16/09/2026 | José Luis Aragón   Cervantes     | Carga y vista previa de imagen promocional binaria.
*      3        | 02/10/2026 | Muse Spark                       | Fechas UTC explícitas (sin deriva por zona) y
*              |            |                                  | veredicto de visibilidad en vivo en el editor.
* ---------------------------------------------------------------------------------------
*/

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CampaignIcon from "@mui/icons-material/Campaign";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SendIcon from "@mui/icons-material/Send";
import { DataGrid } from "@mui/x-data-grid";
import { esES } from "@mui/x-data-grid/locales";
import {
  apiCampaniaActualizar,
  apiCampaniaAdminDetalle,
  apiCampaniaCrear,
  apiCampaniaPublicar,
  apiCampaniaSubirImagen,
  apiCampaniasAdmin,
  apiCampaniasProspectos,
} from "../settings/supportApi";
import { serverSupport } from "../settings/ApiConfig";
import {
  esUrlSegura,
  extraerDriveIdSeguro,
  normalizarImagenSegura,
  sanitizarMultilinea,
  sanitizarTexto,
  sanitizarUrl,
  validarSinInyeccion,
} from "../settings/inputSecurity";

const canales = ["WEB", "FACEBOOK", "INSTAGRAM", "TIKTOK", "PINTEREST", "WHATSAPP", "EMAIL", "QR", "MULTICANAL", "OTRO"];
const imagenMimePermitidos = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const imagenMaxBytes = 8 * 1024 * 1024;
const acciones = ["FORM", "WHATSAPP", "LINK"];

const formInicial = {
  codigo: "",
  nombre: "",
  descripcion: "",
  productoServicio: "ARSync",
  canal: "WEB",
  fechaInicioUtc: "",
  fechaFinUtc: "",
  activa: true,
  landingTitulo: "",
  landingSubtitulo: "",
  landingDescripcion: "",
  imagenUrl: "",
  videoUrl: "",
  ctaTexto: "Solicitar demo",
  actionType: "FORM",
  whatsAppUrl: "",
  whatsAppMensaje: "",
  mostrarFormulario: true,
  beneficiosTexto: "",
};

const limpiar = (valor, limite) => sanitizarTexto(valor, limite);

const extraerGoogleDriveId = (valor) => extraerDriveIdSeguro(valor);

const normalizarImagenDrive = (url) => normalizarImagenSegura(url, 500) || "";

const extraerUrl = (valor, limite, normalizarImagen = false) => {
  if (normalizarImagen) {
    return normalizarImagenSegura(valor, limite);
  }
  return sanitizarUrl(valor, limite);
};

// Endpoint binario directo: funciona aunque la URL absoluta configurada
// en el backend falte o sea inalcanzable desde el navegador.
const urlImagenBinaria = (codigo) => {
  const c = String(codigo || "").trim();
  if (!c || !serverSupport) return "";
  return `${String(serverSupport).replace(/\/$/, "")}/SupportCampaniasPublicas/${encodeURIComponent(c)}/imagen`;
};

const fechaInput = (valor) => {
  if (!valor) return "";
  // El backend entrega UTC; datetime2 no trae zona, así que sin sufijo se asume UTC
  // explícitamente (antes se interpretaba como hora local y cada edición desplazaba las fechas).
  const texto = String(valor).trim();
  const fecha = new Date(/([+-]\d{2}:?\d{2}|Z)$/i.test(texto) ? texto : `${texto}Z`);
  if (Number.isNaN(fecha.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${fecha.getFullYear()}-${p(fecha.getMonth() + 1)}-${p(fecha.getDate())}T${p(fecha.getHours())}:${p(fecha.getMinutes())}`;
};

const fechaSalida = (valor) => {
  if (!valor) return null;
  // Construcción local explícita por componentes (inmune a ambigüedades de parseo).
  const m = String(valor).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return null;
  const fecha = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  return Number.isNaN(fecha.getTime()) ? null : fecha.toISOString();
};

// Equivalente UTC legible de un datetime-local (lo que evaluará la API pública).
const fechaUtcLegible = (valor) => {
  const iso = fechaSalida(valor);
  return iso ? iso.replace("T", " ").slice(0, 16) + " UTC" : "—";
};

// Vigencia real = mismo criterio que la API pública (flag + rango de fechas).
const obtenerVigencia = (row) => {
  if (!row?.activa) return { clave: "PAUSADA", etiqueta: "Pausada", color: "default" };
  const ahora = Date.now();
  const inicio = row?.fechaInicioUtc ? new Date(row.fechaInicioUtc).getTime() : NaN;
  const fin = row?.fechaFinUtc ? new Date(row.fechaFinUtc).getTime() : NaN;
  if (!Number.isNaN(inicio) && inicio > ahora) return { clave: "PROGRAMADA", etiqueta: "Programada", color: "info" };
  if (!Number.isNaN(fin) && fin < ahora) return { clave: "VENCIDA", etiqueta: "Vencida", color: "error" };
  return { clave: "ACTIVA", etiqueta: "Activa", color: "success" };
};

// Campo WhatsApp = solo teléfono. Sin validación de estructura:
// se guardan los dígitos tal cual y el https://wa.me/ se pone en automático.
const extraerDigitosTelefono = (valor) => String(valor || "").replace(/\D/g, "");
const mostrarTelefonoWhatsApp = (valor) => {
  const crudo = String(valor || "").trim();
  if (!crudo) return "";
  const m = crudo.match(/wa\.me\/(\d+)/i);
  if (m?.[1]) return m[1];
  return extraerDigitosTelefono(crudo);
};
const normalizarWhatsAppUrl = (valor, mensaje = "") => {
  const digitos = extraerDigitosTelefono(valor);
  if (!digitos) return "";
  const texto = String(mensaje || "").trim();
  return texto
    ? `https://wa.me/${digitos}?text=${encodeURIComponent(texto.slice(0, 500))}`
    : `https://wa.me/${digitos}`;
};

const CampaniasPage = () => {
  const [campanias, setCampanias] = useState([]);
  const [prospectos, setProspectos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(formInicial);
  const [guardando, setGuardando] = useState(false);
  const [publicandoId, setPublicandoId] = useState(null);
  const [validandoVigencias, setValidandoVigencias] = useState(false);
  const [imagenArchivo, setImagenArchivo] = useState(null);
  const [imagenPreview, setImagenPreview] = useState("");
  const [imagenInfo, setImagenInfo] = useState("");
  const [imagenError, setImagenError] = useState("");
  const fileInputRef = useRef(null);
  const guardandoRef = useRef(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [campaniasData, prospectosData] = await Promise.all([
        apiCampaniasAdmin({ busqueda: limpiar(busqueda, 120) || undefined }),
        apiCampaniasProspectos({ tamano: 100 }),
      ]);
      setCampanias(campaniasData || []);
      setProspectos(prospectosData?.items || []);
    } catch (err) {
      setError(err.message || "No se pudieron cargar las campañas.");
    } finally {
      setLoading(false);
    }
  }, [busqueda]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  useEffect(() => {
    return () => {
      if (imagenPreview?.startsWith("blob:")) {
        URL.revokeObjectURL(imagenPreview);
      }
    };
  }, [imagenPreview]);


  const limpiarSeleccionImagen = () => {
    setImagenArchivo(null);
    setImagenInfo("");
    setImagenError("");
    setImagenPreview((prev) => {
      if (prev?.startsWith("blob:")) {
        URL.revokeObjectURL(prev);
      }
      return "";
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const prepararImagenArchivo = (file) => {
    if (!file) return;

    const tipo = String(file.type || "").toLowerCase();
    if (!imagenMimePermitidos.includes(tipo)) {
      setImagenError("Usa una imagen JPG, PNG, WEBP o GIF.");
      return;
    }

    if (file.size > imagenMaxBytes) {
      setImagenError("La imagen no debe exceder 8 MB.");
      return;
    }

    const preview = URL.createObjectURL(file);
    setImagenPreview((prev) => {
      if (prev?.startsWith("blob:")) {
        URL.revokeObjectURL(prev);
      }
      return preview;
    });
    setImagenArchivo(file);
    setImagenInfo(`${file.name} · ${(file.size / 1024 / 1024).toFixed(2)} MB · ${tipo}`);
    setImagenError("");
    setForm((prev) => ({ ...prev, imagenUrl: "" }));
  };

  const seleccionarImagen = (event) => {
    prepararImagenArchivo(event.target.files?.[0]);
  };

  const pegarImagen = (event) => {
    const file = Array.from(event.clipboardData?.files || []).find((item) => String(item.type || "").startsWith("image/"));
    if (file) {
      event.preventDefault();
      prepararImagenArchivo(file);
    }
  };

  const abrirNuevo = () => {
    limpiarSeleccionImagen();
    setEditId(null);
    setForm({
      ...formInicial,
      codigo: `ARSYNC-${new Date().toISOString().slice(0, 10).replaceAll("-", "")}`,
      fechaInicioUtc: fechaInput(new Date().toISOString()),
    });
    setDialogOpen(true);
  };

  const abrirEditar = async (row) => {
    limpiarSeleccionImagen();
    setError("");
    try {
      const detalle = await apiCampaniaAdminDetalle(row.id);
      setEditId(detalle.id);
      if (detalle.imagenUrl) {
        setImagenPreview(detalle.imagenUrl);
        setImagenInfo(detalle.tieneImagenArchivo ? "Imagen guardada en base de datos." : "Imagen por URL externa.");
      } else if (detalle.tieneImagenArchivo) {
        setImagenPreview(urlImagenBinaria(detalle.codigo));
        setImagenInfo("Imagen guardada en base de datos.");
      }
      setForm({
        codigo: detalle.codigo || "",
        nombre: detalle.nombre || "",
        descripcion: detalle.descripcion || "",
        productoServicio: detalle.productoServicio || "ARSync",
        canal: detalle.canal || "WEB",
        fechaInicioUtc: fechaInput(detalle.fechaInicioUtc),
        fechaFinUtc: fechaInput(detalle.fechaFinUtc),
        activa: Boolean(detalle.activa),
        landingTitulo: detalle.landingTitulo || "",
        landingSubtitulo: detalle.landingSubtitulo || "",
        landingDescripcion: detalle.landingDescripcion || "",
        imagenUrl: detalle.imagenUrl || "",
        videoUrl: detalle.videoUrl || "",
        ctaTexto: detalle.ctaTexto || "Solicitar demo",
        actionType: detalle.actionType || "FORM",
        whatsAppUrl: mostrarTelefonoWhatsApp(detalle.whatsAppUrl),
        whatsAppMensaje: detalle.whatsAppMensaje || "",
        mostrarFormulario: Boolean(detalle.mostrarFormulario),
        beneficiosTexto: (detalle.beneficios || []).map((b) => b.texto).join("\n"),
      });
      setDialogOpen(true);
    } catch (err) {
      setError(err.message || "No se pudo abrir la campaña.");
    }
  };

  const set = (campo) => (event) => {
    const { type, checked, value } = event.target;
    setForm((prev) => ({ ...prev, [campo]: type === "checkbox" ? checked : value }));
  };

  const normalizarUrlCampo = (campo, limite) => () => {
    setForm((prev) => ({ ...prev, [campo]: extraerUrl(prev[campo], limite, campo === "imagenUrl") }));
  };

  const validar = () => {
    if (!limpiar(form.codigo, 60)) return "El código es requerido.";
    if (!limpiar(form.nombre, 180)) return "El nombre de campaña es requerido.";
    if (!limpiar(form.productoServicio, 120)) return "El producto o servicio es requerido.";
    if (!limpiar(form.landingTitulo, 180)) return "El título de landing es requerido.";
    if (form.fechaInicioUtc && form.fechaFinUtc) {
      const inicio = new Date(form.fechaInicioUtc).getTime();
      const fin = new Date(form.fechaFinUtc).getTime();
      if (!Number.isNaN(inicio) && !Number.isNaN(fin) && fin < inicio) {
        return "La fecha final no puede ser menor a la fecha inicial.";
      }
    }
    // Bloqueo de inyección sin romper texto comercial legítimo (acentos, emojis, puntuación).
    const bloqueo = validarSinInyeccion([
      { etiqueta: "Código", valor: form.codigo },
      { etiqueta: "Nombre", valor: form.nombre },
      { etiqueta: "Descripción interna", valor: form.descripcion },
      { etiqueta: "Producto / servicio", valor: form.productoServicio },
      { etiqueta: "Título landing", valor: form.landingTitulo },
      { etiqueta: "Subtítulo landing", valor: form.landingSubtitulo },
      { etiqueta: "Descripción comercial", valor: form.landingDescripcion },
      { etiqueta: "Texto del botón", valor: form.ctaTexto },
      { etiqueta: "Mensaje WhatsApp", valor: form.whatsAppMensaje },
      { etiqueta: "WhatsApp", valor: form.whatsAppUrl },
      { etiqueta: "Beneficios", valor: form.beneficiosTexto },
      { etiqueta: "Imagen URL", valor: form.imagenUrl, esUrl: true },
      { etiqueta: "Video URL", valor: form.videoUrl, esUrl: true },
    ]);
    if (bloqueo) return `Bloqueado por seguridad: ${bloqueo}`;
    // URLs opcionales: si el usuario escribió algo pero no es https válido, bloquear (evita javascript:/data:).
    const urlsOpcionales = [
      { etiqueta: "Imagen URL", valor: form.imagenUrl },
      { etiqueta: "Video URL", valor: form.videoUrl },
    ];
    for (const u of urlsOpcionales) {
      const t = String(u.valor || "").trim();
      if (t && !esUrlSegura(t) && !extraerDriveIdSeguro(t)) {
        return `Bloqueado por seguridad: ${u.etiqueta}: URL no permitida. Usa solo enlaces https:// válidos.`;
      }
    }
    // WhatsApp: sin validación de estructura, solo dígitos (ej. 522411232647).
    return "";
  };

  const guardar = async () => {
    if (guardandoRef.current) return;
    const errorValidacion = validar();
    if (errorValidacion) {
      setError(errorValidacion);
      return;
    }

    guardandoRef.current = true;
    const whatsAppMensajeLimpio = sanitizarMultilinea(form.whatsAppMensaje, 500);
    const payload = {
      codigo: limpiar(form.codigo, 60).toUpperCase(),
      nombre: limpiar(form.nombre, 180),
      descripcion: sanitizarMultilinea(form.descripcion, 1000),
      productoServicio: limpiar(form.productoServicio, 120),
      canal: form.canal,
      fechaInicioUtc: fechaSalida(form.fechaInicioUtc || new Date().toISOString()),
      fechaFinUtc: fechaSalida(form.fechaFinUtc),
      activa: form.activa,
      landingTitulo: limpiar(form.landingTitulo, 180),
      landingSubtitulo: limpiar(form.landingSubtitulo, 300),
      landingDescripcion: sanitizarMultilinea(form.landingDescripcion, 2000),
      imagenUrl: extraerUrl(form.imagenUrl, 500, true),
      videoUrl: extraerUrl(form.videoUrl, 500),
      ctaTexto: limpiar(form.ctaTexto, 80) || "Solicitar demo",
      actionType: form.actionType,
      whatsAppUrl: normalizarWhatsAppUrl(form.whatsAppUrl, whatsAppMensajeLimpio),
      whatsAppMensaje: whatsAppMensajeLimpio,
      mostrarFormulario: form.mostrarFormulario,
      beneficios: form.beneficiosTexto
        .split("\n")
        .map((b) => limpiar(b, 500))
        .filter(Boolean)
        .slice(0, 8),
    };

    setGuardando(true);
    setError("");
    setOk("");
    try {
      const detalleGuardado = editId
        ? await apiCampaniaActualizar(editId, payload)
        : await apiCampaniaCrear(payload);
      const campaniaId = detalleGuardado?.id || editId;

      if (imagenArchivo && campaniaId) {
        try {
          await apiCampaniaSubirImagen(campaniaId, imagenArchivo);
        } catch (errImg) {
          setOk(`${editId ? "Campaña actualizada" : "Campaña creada"} pero la imagen no se guardó: ${errImg.message || "error al subir imagen."}`);
          limpiarSeleccionImagen();
          setDialogOpen(false);
          await cargar();
          return;
        }
      }

      setOk(`${editId ? "Campaña actualizada" : "Campaña creada"}${imagenArchivo ? " con imagen promocional." : "."}`);
      limpiarSeleccionImagen();
      setDialogOpen(false);
      await cargar();
    } catch (err) {
      setError(err.message || "No se pudo guardar la campaña.");
    } finally {
      guardandoRef.current = false;
      setGuardando(false);
    }
  };

  const publicar = async (row) => {
    setPublicandoId(row.id);
    setError("");
    setOk("");
    try {
      const mensajeSeguro = sanitizarMultilinea(row.landingSubtitulo || row.descripcion || "", 500);
      const bloqueo = validarSinInyeccion([{ etiqueta: "Mensaje a publicar", valor: mensajeSeguro }]);
      if (bloqueo) {
        setError(`Bloqueado por seguridad: ${bloqueo}`);
        setPublicandoId(null);
        return;
      }
      const result = await apiCampaniaPublicar(row.id, {
        canales: [row.canal || "WEB"],
        mensaje: mensajeSeguro,
      });
      setOk(`${result.mensaje} CorrelationId: ${result.correlationId}`);
      await cargar();
    } catch (err) {
      setError(err.message || "No se pudo publicar la campaña.");
    } finally {
      setPublicandoId(null);
    }
  };

  const copiarUrl = async (url) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setOk("Liga pública copiada.");
    } catch {
      setOk(url);
    }
  };

  // Validador del monitor: desactiva en BD las que siguen con flag activo pero ya vencieron.
  const campaniasVencidas = campanias.filter((c) => obtenerVigencia(c).clave === "VENCIDA");
  const validarVigencias = async () => {
    if (campaniasVencidas.length === 0) {
      setOk("Sin campañas vencidas por concluir.");
      return;
    }
    setValidandoVigencias(true);
    setError("");
    setOk("");
    let concluidas = 0;
    const fallidas = [];
    for (const row of campaniasVencidas) {
      try {
        const d = await apiCampaniaAdminDetalle(row.id);
        const mensajeLimpio = sanitizarMultilinea(d.whatsAppMensaje || "", 500);
        await apiCampaniaActualizar(row.id, {
          codigo: String(d.codigo || "").toUpperCase(),
          nombre: sanitizarTexto(d.nombre || "", 180),
          descripcion: sanitizarMultilinea(d.descripcion || "", 1000),
          productoServicio: sanitizarTexto(d.productoServicio || "ARSync", 120),
          canal: d.canal || "WEB",
          fechaInicioUtc: fechaSalida(d.fechaInicioUtc || new Date().toISOString()),
          fechaFinUtc: fechaSalida(d.fechaFinUtc),
          activa: false,
          landingTitulo: sanitizarTexto(d.landingTitulo || "", 180),
          landingSubtitulo: sanitizarTexto(d.landingSubtitulo || "", 300),
          landingDescripcion: sanitizarMultilinea(d.landingDescripcion || "", 2000),
          imagenUrl: d.tieneImagenArchivo ? "" : sanitizarUrl(d.imagenUrl || "", 500),
          videoUrl: sanitizarUrl(d.videoUrl || "", 500),
          ctaTexto: sanitizarTexto(d.ctaTexto || "Solicitar demo", 80),
          actionType: d.actionType || "FORM",
          whatsAppUrl: normalizarWhatsAppUrl(d.whatsAppUrl || "", mensajeLimpio),
          whatsAppMensaje: mensajeLimpio,
          mostrarFormulario: Boolean(d.mostrarFormulario),
          beneficios: (d.beneficios || []).map((b) => sanitizarTexto(b.texto || "", 500)).filter(Boolean).slice(0, 8),
        });
        concluidas += 1;
      } catch (err) {
        fallidas.push(`${row.codigo || row.id}: ${err.message || "error"}`);
      }
    }
    await cargar();
    setValidandoVigencias(false);
    if (fallidas.length > 0) {
      setError(`Concluidas ${concluidas}, fallaron ${fallidas.length}: ${fallidas.slice(0, 3).join(" | ")}`);
    } else {
      setOk(`${concluidas} campaña(s) vencida(s) cambiadas a Pausada (concluida).`);
    }
  };

  const columnsCampanias = [
    { field: "codigo", headerName: "Código", width: 150 },
    { field: "nombre", headerName: "Campaña", flex: 1, minWidth: 220 },
    { field: "productoServicio", headerName: "Producto", width: 140 },
    { field: "canal", headerName: "Canal", width: 120 },
    {
      field: "activa",
      headerName: "Estatus",
      width: 130,
      renderCell: (p) => {
        const v = obtenerVigencia(p.row);
        return <Chip size="small" color={v.color} label={v.etiqueta} />;
      },
    },
    {
      field: "fechaFinUtc",
      headerName: "Vigencia fin",
      width: 175,
      valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleString() : "Sin fin"),
    },
    { field: "totalVisitas", headerName: "Visitas", width: 95, type: "number" },
    { field: "totalProspectos", headerName: "Leads", width: 95, type: "number" },
    {
      field: "urlPublica",
      headerName: "Liga",
      width: 130,
      sortable: false,
      renderCell: (p) => (
        <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => copiarUrl(p.value)}>
          Copiar
        </Button>
      ),
    },
    {
      field: "acciones",
      headerName: "Acciones",
      width: 230,
      sortable: false,
      renderCell: (p) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" onClick={() => abrirEditar(p.row)}>
            Editar
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<SendIcon />}
            disabled={publicandoId === p.row.id}
            onClick={() => publicar(p.row)}
          >
            n8n
          </Button>
        </Stack>
      ),
    },
  ];

  const columnsProspectos = [
    { field: "codigoProspecto", headerName: "ID prospecto", width: 170 },
    { field: "nombre", headerName: "Nombre", width: 180 },
    { field: "nombreNegocio", headerName: "Negocio", width: 180 },
    { field: "whatsApp", headerName: "WhatsApp", width: 140 },
    { field: "email", headerName: "Correo", width: 220 },
    { field: "codigoCampania", headerName: "Campaña", width: 150 },
    { field: "canal", headerName: "Canal", width: 120 },
    { field: "estatusProspecto", headerName: "Estatus", width: 150 },
    {
      field: "fechaRegistroUtc",
      headerName: "Registro",
      width: 190,
      valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleString() : ""),
    },
  ];

  return (
    <Box>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>
            Promocionales ARSync
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Administra campañas públicas, captura prospectos y publica hacia n8n.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNuevo}>
          Nueva campaña
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {ok && (
        <Alert severity="success" onClose={() => setOk("")} sx={{ mb: 2 }}>
          {ok}
        </Alert>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }} alignItems="center">
        <TextField
          label="Buscar campaña"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          size="small"
          sx={{ minWidth: 280 }}
        />
        <Button variant="outlined" onClick={cargar} startIcon={<CampaignIcon />}>
          Actualizar
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={validarVigencias}
          disabled={validandoVigencias || loading || campaniasVencidas.length === 0}
        >
          {validandoVigencias ? "Validando..." : `Validar vigencias (${campaniasVencidas.length} vencidas)`}
        </Button>
        {campaniasVencidas.length > 0 && (
          <Typography variant="caption" color="warning.main">
            Hay {campaniasVencidas.length} activa(s) con fecha fin pasada: se cambiarán a Pausada.
          </Typography>
        )}
      </Stack>

      <Box sx={{ height: 430, mb: 4 }}>
        <DataGrid
          rows={campanias}
          columns={columnsCampanias}
          loading={loading}
          getRowId={(r) => r.id}
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          disableRowSelectionOnClick
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" fontWeight={800} gutterBottom>
        Prospectos capturados
      </Typography>
      <Box sx={{ height: 430 }}>
        <DataGrid
          rows={prospectos}
          columns={columnsProspectos}
          loading={loading}
          getRowId={(r) => r.id}
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          disableRowSelectionOnClick
        />
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editId ? "Editar campaña" : "Nueva campaña"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {(() => {
              const v = obtenerVigencia({ activa: form.activa, fechaInicioUtc: form.fechaInicioUtc, fechaFinUtc: form.fechaFinUtc });
              const detalleUtc = `Inicio ${fechaUtcLegible(form.fechaInicioUtc)} · Fin ${form.fechaFinUtc ? fechaUtcLegible(form.fechaFinUtc) : "sin fin"}`;
              if (v.clave === "VENCIDA") {
                return <Alert severity="warning">Esta campaña está vencida por fecha fin aunque el flag siga activo: la liga pública mostrará “Campaña no disponible”. Extiende la fecha fin o desactívala.<br />{detalleUtc}</Alert>;
              }
              if (v.clave === "PROGRAMADA") {
                return <Alert severity="info">Esta campaña aún no inicia: la liga pública estará disponible a partir del inicio.<br />{detalleUtc}</Alert>;
              }
              if (v.clave === "PAUSADA") {
                return <Alert severity="info">Campaña pausada por flag: la liga pública mostrará “Campaña no disponible”.<br />{detalleUtc}</Alert>;
              }
              return <Alert severity="success">Visible al público ahora.<br />{detalleUtc}</Alert>;
            })()}
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField label="Código" value={form.codigo} onChange={set("codigo")} fullWidth inputProps={{ maxLength: 60 }} />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField label="Nombre" value={form.nombre} onChange={set("nombre")} fullWidth inputProps={{ maxLength: 180 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Producto / servicio" value={form.productoServicio} onChange={set("productoServicio")} fullWidth inputProps={{ maxLength: 120 }} />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField label="Canal" value={form.canal} onChange={set("canal")} select fullWidth>
                  {canales.map((canal) => (
                    <MenuItem key={canal} value={canal}>
                      {canal}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField label="Acción" value={form.actionType} onChange={set("actionType")} select fullWidth>
                  {acciones.map((accion) => (
                    <MenuItem key={accion} value={accion}>
                      {accion}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Inicio (hora local)"
                  type="datetime-local"
                  value={form.fechaInicioUtc}
                  onChange={set("fechaInicioUtc")}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  helperText={form.fechaInicioUtc ? `Se evalúa como ${fechaUtcLegible(form.fechaInicioUtc)}` : " "}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Fin (hora local)"
                  type="datetime-local"
                  value={form.fechaFinUtc}
                  onChange={set("fechaFinUtc")}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  helperText={form.fechaFinUtc ? `Se evalúa como ${fechaUtcLegible(form.fechaFinUtc)}` : "Sin fin = sin caducidad"}
                />
              </Grid>
            </Grid>

            <TextField label="Título landing" value={form.landingTitulo} onChange={set("landingTitulo")} fullWidth inputProps={{ maxLength: 180 }} />
            <TextField label="Subtítulo landing" value={form.landingSubtitulo} onChange={set("landingSubtitulo")} fullWidth inputProps={{ maxLength: 300 }} />
            <TextField
              label="Descripción comercial"
              value={form.landingDescripcion}
              onChange={set("landingDescripcion")}
              fullWidth
              multiline
              minRows={3}
              inputProps={{ maxLength: 2000 }}
            />
            <TextField label="Descripción interna" value={form.descripcion} onChange={set("descripcion")} fullWidth inputProps={{ maxLength: 1000 }} />

            <Paper
              variant="outlined"
              onPaste={pegarImagen}
              sx={{ p: 2, borderStyle: "dashed", borderColor: imagenError ? "error.main" : "divider", bgcolor: "#fbfdff" }}
            >
              <Stack spacing={1.5}>
                <Box>
                  <Typography fontWeight={800}>Imagen promocional para redes</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Selecciona o pega una imagen. Se guarda en base de datos y n8n la recibe como archivo para publicar físicamente. GIF animado permitido.
                  </Typography>
                </Box>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button variant="outlined" onClick={() => fileInputRef.current?.click()}>
                    Seleccionar imagen
                  </Button>
                  <Button variant="text" onClick={limpiarSeleccionImagen} disabled={!imagenPreview && !imagenArchivo}>
                    Quitar selección
                  </Button>
                </Stack>
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={seleccionarImagen}
                />
                {imagenError && <Alert severity="error">{imagenError}</Alert>}
                {imagenInfo && <Typography variant="caption" color="text.secondary">{imagenInfo}</Typography>}
                {(imagenPreview || form.imagenUrl) && (() => {
                  const crudo = imagenPreview || form.imagenUrl;
                  // blob: es preview local generado por URL.createObjectURL (seguro, no se guarda en BD).
                  const srcSeguro = String(crudo || "").startsWith("blob:")
                    ? crudo
                    : normalizarImagenDrive(crudo);
                  if (!srcSeguro) return null;
                  const respaldoBinario = urlImagenBinaria(form.codigo);
                  return (
                    <Box
                      component="img"
                      src={srcSeguro}
                      alt="Vista previa promocional"
                      onError={(event) => {
                        if (respaldoBinario && event.currentTarget.src !== respaldoBinario) {
                          event.currentTarget.src = respaldoBinario;
                          return;
                        }
                        event.currentTarget.style.display = "none";
                      }}
                      sx={{ width: "100%", maxHeight: 260, objectFit: "contain", borderRadius: 2, bgcolor: "#eef2f7" }}
                    />
                  );
                })()}
              </Stack>
            </Paper>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField label="Imagen URL opcional / respaldo" value={form.imagenUrl} onChange={set("imagenUrl")} onBlur={normalizarUrlCampo("imagenUrl", 500)} fullWidth inputProps={{ maxLength: 500 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Video URL" value={form.videoUrl} onChange={set("videoUrl")} onBlur={normalizarUrlCampo("videoUrl", 500)} fullWidth inputProps={{ maxLength: 500 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Texto botón" value={form.ctaTexto} onChange={set("ctaTexto")} fullWidth inputProps={{ maxLength: 80 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="WhatsApp (solo teléfono, ej. 522411232647)" value={form.whatsAppUrl} onChange={set("whatsAppUrl")} fullWidth inputProps={{ maxLength: 20, inputMode: "tel" }} helperText="Se guarda el número y el enlace https://wa.me/ se genera solo." />
              </Grid>
            </Grid>
            <TextField label="Mensaje WhatsApp opcional" value={form.whatsAppMensaje} onChange={set("whatsAppMensaje")} fullWidth inputProps={{ maxLength: 500 }} />
            <TextField
              label="Beneficios, uno por línea"
              value={form.beneficiosTexto}
              onChange={set("beneficiosTexto")}
              fullWidth
              multiline
              minRows={3}
            />
            <Stack direction="row" spacing={2}>
              <FormControlLabel control={<Checkbox checked={form.activa} onChange={set("activa")} />} label="Campaña activa" />
              <FormControlLabel control={<Checkbox checked={form.mostrarFormulario} onChange={set("mostrarFormulario")} />} label="Mostrar formulario" />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { limpiarSeleccionImagen(); setDialogOpen(false); }}>Cancelar</Button>
          <Button variant="contained" onClick={guardar} disabled={guardando}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CampaniasPage;
